import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import { Form, Button } from 'react-bootstrap';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { MdDoubleArrow } from 'react-icons/md';
import instance from "../../services/baseService";
import toast from "react-hot-toast";
import { CRUD_PERMISSIONS } from "../../constants/user";
import { hasPermission } from '../../utils/permissions';

const OnboardHierarchies = () => {
  const { permissions } = useSelector((state) => state.common);
  // Dynamic role options for each card
  const [roleOptions, setRoleOptions] = useState({
    user: {},
    client: {},
    hsn: {}
  });

  // Loading state for role options
  const [loadingRoles, setLoadingRoles] = useState({
    user: {},
    client: {},
    hsn: {}
  });

  // Loading state for save operations
  const [savingRoles, setSavingRoles] = useState({
    user: false,
    client: false,
    hsn: false
  });

  // State for tracking roles in each card - now storing both id and name
  const [cardRoles, setCardRoles] = useState({
    user: [{ id: 0, value: '', selectedRoleId: null, error: false }],
    client: [{ id: 0, value: '', selectedRoleId: null, error: false }],
    hsn: [{ id: 0, value: '', selectedRoleId: null, error: false }]
  });

  // State to store original data before editing
  const [originalCardRoles, setOriginalCardRoles] = useState({
    user: [{ id: 0, value: '', selectedRoleId: null, error: false }],
    client: [{ id: 0, value: '', selectedRoleId: null, error: false }],
    hsn: [{ id: 0, value: '', selectedRoleId: null, error: false }]
  });

  const [nextId, setNextId] = useState({
    user: 1,
    client: 1,
    hsn: 1
  });

  // State for tracking edit mode for each card
  const [editMode, setEditMode] = useState({
    user: false,
    client: false,
    hsn: false
  });

  // API call to fetch roles
  const fetchRoles = async (cardType, roleIndex, parentRoleId = null) => {
    try {
      setLoadingRoles(prev => ({
        ...prev,
        [cardType]: { ...prev[cardType], [roleIndex]: true }
      }));

      const endpoint = parentRoleId
        ? `/api/v1/rbac/approval/roles/list/${parentRoleId}`
        : `/api/v1/rbac/approval/roles/list`;

      const response = await instance.get(endpoint);

      if (response.data?.type === 'success') {
        setRoleOptions(prev => ({
          ...prev,
          [cardType]: {
            ...prev[cardType],
            [roleIndex]: response.data.data
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    } finally {
      setLoadingRoles(prev => ({
        ...prev,
        [cardType]: { ...prev[cardType], [roleIndex]: false }
      }));
    }
  };

  // API call to fetch existing hierarchy data
  const fetchExistingHierarchy = async (cardType) => {
    try {
      let endpoint;
      switch (cardType) {
        case 'user':
          endpoint = '/api/v1/view/user/approval';
          break;
        case 'client':
          endpoint = '/api/v1/client/view/onboard';
          break;
        case 'hsn':
          endpoint = '/api/v1/hsnrepo/view/level';
          break;
        default:
          return;
      }

      const response = await instance.get(endpoint);

      if (response.data?.type === 'success' && response.data.data?.approver_levels) {
        const approverLevels = response.data.data.approver_levels;

        // Sort by level in descending order (higher level first)
        const sortedLevels = approverLevels.sort((a, b) => b.level - a.level);

        if (sortedLevels.length > 0) {
          // Create role objects for each level
          const existingRoles = sortedLevels.map((level, index) => ({
            id: index,
            value: level.name,
            selectedRoleId: level.id,
            error: false
          }));

          // Update cardRoles with existing data
          setCardRoles(prev => ({
            ...prev,
            [cardType]: existingRoles
          }));

          // Also update originalCardRoles to store the initial state
          setOriginalCardRoles(prev => ({
            ...prev,
            [cardType]: existingRoles
          }));

          // Update nextId counter
          setNextId(prev => ({
            ...prev,
            [cardType]: sortedLevels.length
          }));
        }
      }
    } catch (error) {
      console.error(`Error fetching existing ${cardType} hierarchy:`, error);
    }
  };

  // Load existing hierarchies when component mounts
  useEffect(() => {
    fetchExistingHierarchy('user');
    fetchExistingHierarchy('client');
    fetchExistingHierarchy('hsn');
  }, []);

  // Load roles for existing data when entering edit mode
  const loadRolesForExistingData = async (cardType) => {
    const roles = cardRoles[cardType];

    // First, fetch roles for the first dropdown
    await fetchRoles(cardType, 0);

    // Then fetch roles for subsequent dropdowns based on parent selections
    for (let i = 1; i < roles.length; i++) {
      const parentRole = roles[i - 1];
      if (parentRole.selectedRoleId) {
        await fetchRoles(cardType, i, parentRole.selectedRoleId);
      }
    }
  };

  const toggleEditMode = async (cardType) => {
    const newEditMode = !editMode[cardType];

    if (newEditMode) {
      // Entering edit mode - store current state as original
      setOriginalCardRoles(prev => ({
        ...prev,
        [cardType]: [...cardRoles[cardType]]
      }));

      // Load roles for existing data
      await loadRolesForExistingData(cardType);
    }

    setEditMode(prev => ({
      ...prev,
      [cardType]: newEditMode
    }));
  };

  const addRole = (cardType, currentIndex) => {
    const currentRole = cardRoles[cardType].find(role => role.id === currentIndex);

    // Validate current selection
    if (!currentRole.value || !currentRole.selectedRoleId) {
      setCardRoles(prev => ({
        ...prev,
        [cardType]: prev[cardType].map(role =>
          role.id === currentIndex ? { ...role, error: true } : role
        )
      }));
      return;
    }

    // Clear error and add new role
    const newRoleId = nextId[cardType];
    setCardRoles(prev => ({
      ...prev,
      [cardType]: [
        ...prev[cardType].map(role =>
          role.id === currentIndex ? { ...role, error: false } : role
        ),
        { id: newRoleId, value: '', selectedRoleId: null, error: false }
      ]
    }));

    setNextId(prev => ({
      ...prev,
      [cardType]: prev[cardType] + 1
    }));

    // Fetch parent roles for the new dropdown
    fetchRoles(cardType, newRoleId, currentRole.selectedRoleId);
  };

  const deleteRole = (cardType, roleId) => {
    setCardRoles(prev => ({
      ...prev,
      [cardType]: prev[cardType].filter(role => role.id !== roleId)
    }));

    // Clean up role options for deleted role
    setRoleOptions(prev => ({
      ...prev,
      [cardType]: Object.keys(prev[cardType]).reduce((acc, key) => {
        if (parseInt(key) !== roleId) {
          acc[key] = prev[cardType][key];
        }
        return acc;
      }, {})
    }));
  };

  const updateRole = (cardType, roleId, value) => {
  // Find the selected role object
  const roleIndex = cardRoles[cardType].findIndex(role => role.id === roleId);
  const availableRoles = roleOptions[cardType][roleId] || [];
  const selectedRole = availableRoles.find(role => role.name === value);

  // Update the current role
  setCardRoles(prev => ({
    ...prev,
    [cardType]: prev[cardType].map(role =>
      role.id === roleId ? {
        ...role,
        value,
        selectedRoleId: selectedRole ? selectedRole.id : null,
        error: false
      } : role
    )
  }));

  // Get current roles array
  const currentRoles = cardRoles[cardType];
  const currentRoleIndex = currentRoles.findIndex(role => role.id === roleId);

  // Keep current node + next node (so slice to currentRoleIndex + 2)
  setCardRoles(prev => ({
    ...prev,
    [cardType]: prev[cardType].slice(0, currentRoleIndex + 2)
  }));

  // Update nextId counter to reflect the new array length
  setNextId(prev => ({
    ...prev,
    [cardType]: currentRoleIndex + 2
  }));

  // Clean up role options for removed roles (keep options for current and next node only)
  setRoleOptions(prev => ({
    ...prev,
    [cardType]: Object.keys(prev[cardType]).reduce((acc, key) => {
      const keyIndex = currentRoles.findIndex(role => role.id === parseInt(key));
      if (keyIndex <= currentRoleIndex + 1) { // Keep current and next node options
        acc[key] = prev[cardType][key];
      }
      return acc;
    }, {})
  }));

  // Clear the next node's selection but keep the node itself
  if (currentRoleIndex + 1 < cardRoles[cardType].length) {
    setCardRoles(prev => ({
      ...prev,
      [cardType]: prev[cardType].map((role, index) =>
        index === currentRoleIndex + 1 ? { 
          ...role, 
          value: '', 
          selectedRoleId: null, 
          error: false 
        } : role
      )
    }));
  }

  // Auto-fetch options for the immediate next role if it exists and we have a selection
  if (selectedRole && currentRoleIndex + 1 < cardRoles[cardType].length) {
    const nextRole = cardRoles[cardType][currentRoleIndex + 1];
    if (nextRole) {
      fetchRoles(cardType, nextRole.id, selectedRole.id);
    }
  }
};

  // API call to save roles based on card type
  const saveRoles = async (cardType, roleIds) => {
    try {
      let endpoint;
      const requestData = { approver_levels: roleIds };

      switch (cardType) {
        case 'user':
          endpoint = '/api/v1/set/user/approval';
          break;
        case 'client':
          endpoint = '/api/v1/client/set/onboard';
          break;
        case 'hsn':
          endpoint = '/api/v1/hsnrepo/insert/level';
          break;
        default:
          throw new Error('Invalid card type');
      }

      const response = await instance.post(endpoint, requestData);

      if (response.data?.type === 'success') {
        return response.data;
      } else {
        throw new Error(response.data?.message || 'Save failed');
      }
    } catch (error) {
      console.error(`Error saving ${cardType} roles:`, error);
      throw error;
    }
  };

  const handleSave = async (cardType) => {
    try {
      setSavingRoles(prev => ({
        ...prev,
        [cardType]: true
      }));

      // Validate that all roles are selected
      const roles = cardRoles[cardType];
      const hasErrors = roles.some(role => !role.value || !role.selectedRoleId);

      if (hasErrors) {
        // Mark roles with errors
        setCardRoles(prev => ({
          ...prev,
          [cardType]: prev[cardType].map(role => ({
            ...role,
            error: !role.value || !role.selectedRoleId
          }))
        }));
        toast.error('Please select all roles before saving.');
        return;
      }

      // Extract role IDs for API call
      const roleIds = roles
        .filter(role => role.selectedRoleId)
        .map(role => role.selectedRoleId);

      if (roleIds.length === 0) {
        toast.error('No roles selected to save.');
        return;
      }

      // Call the appropriate save API
      const response = await saveRoles(cardType, roleIds);

      // Show success message
      const cardTitles = {
        user: 'User Onboard',
        client: 'Client Onboard',
        hsn: 'HSN'
      };
      toast.success(`${cardTitles[cardType]} hierarchy saved successfully!`);

      // Update original state to reflect saved changes
      setOriginalCardRoles(prev => ({
        ...prev,
        [cardType]: [...cardRoles[cardType]]
      }));

      // Disable edit mode after saving
      setEditMode(prev => ({
        ...prev,
        [cardType]: false
      }));

      // Refresh the hierarchy data to show updated listings
      await fetchExistingHierarchy(cardType);

    } catch (error) {
      console.error(`Error saving ${cardType} roles:`, error);
      toast.error(`Failed to save ${cardType} hierarchy. Please try again.`);
    } finally {
      setSavingRoles(prev => ({
        ...prev,
        [cardType]: false
      }));
    }
  };

  const handleCancel = (cardType) => {
    // Restore to original state
    setCardRoles(prev => ({
      ...prev,
      [cardType]: [...originalCardRoles[cardType]]
    }));

    // Clear role options
    setRoleOptions(prev => ({
      ...prev,
      [cardType]: {}
    }));

    // Disable edit mode after cancelling
    setEditMode(prev => ({
      ...prev,
      [cardType]: false
    }));
  };

  const renderCard = (cardType, title, label) => {
    const roles = cardRoles[cardType];
    const lastRoleIndex = roles.length - 1;
    const isEditMode = editMode[cardType];
    const isSaving = savingRoles[cardType];

    return (
      <div key={cardType} className="mb-4">
        <fieldset className="border rounded p-3 bg-white shadow-sm">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <MdDoubleArrow className="text-primary" />
              <legend className="fw-bold h6 text-primary mb-0">{title}</legend>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => toggleEditMode(cardType)}
              className="d-flex align-items-center no-hover"
              disabled={isSaving}
            >
              <FaEdit className="me-1" />
              <span>Edit</span>
            </Button>
          </div>
          <div>
            <div className="mb-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {roles.map((role, index) => (
                  <React.Fragment key={role.id}>
                    {index > 0 && (
                      <span className="text-primary fw-bold fs-4 mx-1">⟶</span>
                    )}
                    <div className="position-relative">
                      {/* Show text label if it's not the first role and has empty options */}
                      {index > 0 &&
                        !loadingRoles[cardType][role.id] &&
                        roleOptions[cardType][role.id] &&
                        roleOptions[cardType][role.id].length === 0 ? (
                        <div
                          className="d-flex align-items-center justify-content-center bg-light border rounded text-muted"
                          style={{ width: '300px', height: '38px', fontSize: '14px' }}
                        >
                          Top hierarchy reached.Delete before saving.
                        </div>
                      ) : (
                        <Form.Select
                          size="md"
                          isInvalid={role.error}
                          style={{ width: '200px' }}
                          value={role.value}
                          onChange={(e) => updateRole(cardType, role.id, e.target.value)}
                          disabled={!isEditMode || loadingRoles[cardType][role.id] || isSaving}
                        >
                          <option value="">
                            {loadingRoles[cardType][role.id] ? 'Loading...' : 'Select Role'}
                          </option>
                          {(roleOptions[cardType][role.id] || []).map((option, optIndex) => (
                            <option key={optIndex} value={option.name}>{option.name}</option>
                          ))}
                          {/* Show the current value as an option even if not in edit mode */}
                          {!isEditMode && role.value && !(roleOptions[cardType][role.id] || []).some(option => option.name === role.value) && (
                            <option key="current-value" value={role.value}>{role.value}</option>
                          )}
                        </Form.Select>
                      )}
                      {role.error && (
                        <div className="position-absolute text-danger small mt-1" style={{ whiteSpace: 'nowrap', top: '100%', left: 0 }}>
                          Please select a role.
                        </div>
                      )}
                    </div>
                    {index === lastRoleIndex && isEditMode && (
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="d-flex align-items-center justify-content-center no-hover"
                          style={{ width: '28px', height: '28px' }}
                          onClick={() => addRole(cardType, role.id)}
                          title="Add Role"
                          disabled={isSaving}
                        >
                          <FaPlus />
                        </Button>
                        {roles.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="d-flex align-items-center justify-content-center no-hover"
                            style={{ width: '28px', height: '28px' }}
                            onClick={() => deleteRole(cardType, role.id)}
                            title="Remove Role"
                            disabled={isSaving}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Save and Cancel Buttons - Only show in edit mode */}
            {isEditMode && (
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSave(cardType)}
                  title="Save Changes"
                  style={{ width: '100px' }}
                  className='fw-bold'
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCancel(cardType)}
                  title="Cancel Changes"
                  style={{ width: '100px' }}
                  className='fw-bold'
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </fieldset>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="p-2">
            {hasPermission(permissions, CRUD_PERMISSIONS.common_approval.user_onboard) && renderCard('user', 'User Onboard Hierarchy')}
            {hasPermission(permissions, CRUD_PERMISSIONS.common_approval.client_onboard) && renderCard('client', 'Client Onboard Hierarchy',)}
            {hasPermission(permissions, CRUD_PERMISSIONS.common_approval.hsn_approval) && renderCard('hsn', 'HSN Hierarchy',)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardHierarchies;